/**
 * Клиенты комплекса для пикера (без глобального getUsers).
 * Строится из заявок / абонементов / персоналок / членов групп.
 */

import {
  aggregateSectionClients,
  formatUserLabel,
  getUserIdFromRequest,
  normalizeListResponse,
} from './sectionClientsAggregate';

export { formatUserLabel, normalizeListResponse, aggregateSectionClients };

/**
 * Собирает map userId → { id, name, email, phoneNumber } из известных payload'ов.
 */
export function buildClientsUserMap({
  requests = [],
  abonements = [],
  personalTrainings = [],
  groupMembers = [],
  extraUsers = [],
} = {}) {
  const map = {};

  const put = (id, patch) => {
    if (id == null || Number.isNaN(Number(id))) return;
    const n = Number(id);
    const prev = map[n] || { id: n };
    map[n] = {
      ...prev,
      id: n,
      name: patch.name || prev.name || '',
      email: patch.email || prev.email || '',
      phoneNumber: patch.phoneNumber || prev.phoneNumber || '',
    };
  };

  (extraUsers || []).forEach((u) => {
    if (!u) return;
    put(u.id != null ? u.id : u.Id, {
      name: u.name || u.Name || u.userName || u.UserName,
      email: u.email || u.Email,
      phoneNumber: u.phoneNumber || u.PhoneNumber || u.phone || u.Phone,
    });
  });

  (groupMembers || []).forEach((m) => {
    put(m.userId != null ? m.userId : m.UserId, {
      name: m.userName || m.UserName,
      email: m.userEmail || m.UserEmail,
      phoneNumber: m.userPhone || m.UserPhone,
    });
  });

  (requests || []).forEach((r) => {
    const uid = getUserIdFromRequest(r);
    put(uid, {
      name: r.userName || r.UserName || (r.user && (r.user.name || r.user.Name)),
      email: r.userEmail || r.UserEmail || (r.user && (r.user.email || r.user.Email)),
      phoneNumber:
        r.userPhone
        || r.UserPhone
        || r.userLogin
        || r.UserLogin
        || (r.user && (r.user.phoneNumber || r.user.PhoneNumber)),
    });
  });

  (abonements || []).forEach((a) => {
    put(a.userId != null ? a.userId : a.UserId, {
      name: a.userName || a.UserName,
      email: a.userEmail || a.UserEmail,
      phoneNumber: a.userPhone || a.UserPhone || a.userLogin || a.UserLogin,
    });
  });

  (personalTrainings || []).forEach((p) => {
    put(p.userId != null ? p.userId : p.UserId, {
      name: p.userName || p.UserName,
      email: p.userEmail || p.UserEmail,
      phoneNumber: p.userPhone || p.UserPhone || p.userLogin || p.UserLogin,
    });
  });

  return map;
}

export function isPaidInPeriod(abonement, now = new Date()) {
  if (!abonement) return false;
  const paid = abonement.isPaid === true || abonement.IsPaid === true;
  if (!paid) return false;
  const from = abonement.dateFrom || abonement.DateFrom;
  const to = abonement.dateTo || abonement.DateTo;
  if (!from || !to) return paid;
  const t = now.getTime();
  const fromT = new Date(from).getTime();
  const toT = new Date(to).getTime();
  if (Number.isNaN(fromT) || Number.isNaN(toT)) return paid;
  // DateTo обычно конец дня включительно — сравниваем по календарным датам грубо
  return t >= fromT && t <= toT + 24 * 60 * 60 * 1000 - 1;
}

function abonementHasSection(abonement, sectionId) {
  if (sectionId == null || sectionId === '') return true;
  const sid = Number(sectionId);
  const ids = abonement.sectionIds || abonement.SectionIds || [];
  return (ids || []).some((x) => Number(x) === sid);
}

export function collectPaidUserIds(abonements = [], sectionId = null, now = new Date()) {
  const set = new Set();
  (abonements || []).forEach((a) => {
    if (!isPaidInPeriod(a, now) || !abonementHasSection(a, sectionId)) return;
    const uid = a.userId != null ? a.userId : a.UserId;
    if (uid != null && !Number.isNaN(Number(uid))) set.add(Number(uid));
  });
  return set;
}

/**
 * @returns {{ userId: number, label: string, sources: string[], hasPaidAbonementForSection: boolean }[]}
 */
export function buildComplexClientOptions({
  requests = [],
  abonements = [],
  personalTrainings = [],
  groupMembers = [],
  userMap = {},
  sectionId = null,
  now = new Date(),
} = {}) {
  const rows = aggregateSectionClients(requests, abonements, personalTrainings, groupMembers);
  return rows
    .map((row) => {
      const u = userMap[row.userId] || { id: row.userId };
      const hasPaid = (abonements || []).some(
        (a) =>
          Number(a.userId != null ? a.userId : a.UserId) === Number(row.userId)
          && isPaidInPeriod(a, now)
          && abonementHasSection(a, sectionId),
      );
      return {
        userId: row.userId,
        label: formatUserLabel(u) || `Клиент #${row.userId}`,
        sources: row.sources,
        hasPaidAbonementForSection: hasPaid,
      };
    })
    .sort((a, b) => {
      if (a.hasPaidAbonementForSection !== b.hasPaidAbonementForSection) {
        return a.hasPaidAbonementForSection ? -1 : 1;
      }
      return a.label.localeCompare(b.label, 'ru');
    });
}

export function filterComplexClients(options, query) {
  const q = String(query || '').trim().toLowerCase();
  if (!q) return options || [];
  return (options || []).filter((opt) => {
    const hay = `${opt.label || ''} ${opt.userId || ''} ${(opt.sources || []).join(' ')}`.toLowerCase();
    return hay.includes(q);
  });
}

export function clientLabelFromRow(row, userMap = {}) {
  if (!row) return '—';
  const uid = row.userId != null ? row.userId : row.UserId;
  const name = row.userName || row.UserName || '';
  const login = row.userLogin || row.UserLogin || '';
  const phone = row.userPhone || row.UserPhone || login || '';
  const email = row.userEmail || row.UserEmail || '';
  const parts = [];
  [name, phone || email].forEach((part) => {
    if (part && parts.indexOf(part) === -1) parts.push(part);
  });
  if (parts.length) return parts.join(' · ');
  if (uid != null && userMap[Number(uid)]) {
    return formatUserLabel(userMap[Number(uid)]) || `Клиент #${uid}`;
  }
  return uid != null ? `Клиент #${uid}` : '—';
}
